// src/pages/admin/PageUsers.jsx
import { useState, useEffect } from "react";
import * as api from "../../apiService";
import { useToast } from "../../components/Toast";

export function PageUsers() {
  const showToast = useToast();
  const [users,    setUsers]    = useState([]);
  const [roles,    setRoles]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [showAdd,  setShowAdd]  = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [hovCol,   setHovCol]   = useState(null);

  async function load() {
    setLoading(true); setError(null);
    try { const [u,r] = await Promise.all([api.fetchUsers(), api.fetchRoles()]); setUsers(u); setRoles(r); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function toggleActive(email, active) {
    const previous = users;
    setUsers(u => u.map(x => x.email === email ? {...x, active} : x));
    try {
      await api.setUserActive(email, active);
      showToast(active ? "User activated" : "User deactivated");
    } catch (e) {
      setUsers(previous);
      alert("Failed to update user: " + e.message);
    }
  }
  async function handleGrant(email, roleCode) { await api.grantRole(email, roleCode); await load(); showToast("Role granted"); }
  async function handleRevoke(email, roleCode) { await api.revokeRole(email, roleCode); await load(); showToast("Role revoked", "info"); }

  async function handleUpdateName(email, name) {
    try {
      const updated = await api.updateUserName(email, name);
      setUsers(u => u.map(x => x.email === email ? { ...x, name: updated.name } : x));
      setEditUser(null);
      showToast("Name updated");
    } catch (e) {
      alert("Failed to update name: " + e.message);
    }
  }

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Users</div>
          <div className="page-subtitle">Manage access and roles</div>
        </div>
        <div className="page-header-right">
          <button className="btn-primary" onClick={() => setShowAdd(true)}>+ Add User</button>
        </div>
      </div>
      {loading && <div style={{ color:"#aaa", fontSize:12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}
      <div className="card">
        <div className="table-wrap">
        <table className="data-table" data-hov-col={hovCol ?? undefined} onMouseLeave={() => setHovCol(null)}>
          <thead><tr>
            <th>Email</th><th>Name</th><th>Status</th><th>Roles</th><th></th>
          </tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.email}>
                <td onMouseEnter={() => setHovCol(0)}>{u.email}</td>
                <td onMouseEnter={() => setHovCol(1)}>{u.name||"—"}</td>
                <td onMouseEnter={() => setHovCol(2)}>
                  <span className={`badge badge-${u.active?"green":"gray"}`}>{u.active?"Active":"Inactive"}</span>
                </td>
                <td onMouseEnter={() => setHovCol(3)}>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                    {(u.roles||[]).map(r => (
                      <span key={r} className="badge badge-purple" style={{ cursor:"pointer" }} title="Click to revoke"
                        onClick={() => handleRevoke(u.email, r)}>{r} ✕</span>
                    ))}
                    <select className="form-select" style={{ fontSize:11, padding:"2px 6px" }}
                      value="" onChange={e => { if (e.target.value) handleGrant(u.email, e.target.value); }}>
                      <option value="">+ role</option>
                      {roles.filter(r => !(u.roles||[]).includes(r.code)).map(r => (
                        <option key={r.code} value={r.code}>{r.code}</option>
                      ))}
                    </select>
                  </div>
                </td>
                <td onMouseEnter={() => setHovCol(4)} style={{ display:"flex", gap:6 }}>
                  <button className="btn-small" onClick={() => setEditUser(u)}>Edit</button>
                  <button className="btn-small" onClick={() => toggleActive(u.email, !u.active)}>
                    {u.active ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      {showAdd && (
        <div className="overlay" onClick={e => { if (e.target===e.currentTarget) setShowAdd(false); }}>
          <AddUserModal
            roles={roles}
            onSave={async body => { await api.createUser(body); await load(); setShowAdd(false); showToast("User created"); }}
            onCancel={() => setShowAdd(false)}
          />
        </div>
      )}
      {editUser && (
        <div className="overlay" onClick={e => { if (e.target===e.currentTarget) setEditUser(null); }}>
          <EditUserModal
            user={editUser}
            onSave={(email, name) => handleUpdateName(email, name)}
            onCancel={() => setEditUser(null)}
          />
        </div>
      )}
    </>
  );
}

function EditUserModal({ user, onSave, onCancel }) {
  const [name, setName] = useState(user.name || "");
  return (
    <div className="modal">
      <div className="modal-header">
        <div className="modal-title">Edit User</div>
        <button className="btn-small" onClick={onCancel}>✕</button>
      </div>
      <div className="form-row">
        <span className="form-label">Email</span>
        <span style={{ flex:1, fontSize:13, color:"#6b7280" }}>{user.email}</span>
      </div>
      <div className="form-row">
        <span className="form-label">Name</span>
        <input className="form-input" style={{ flex:1 }} value={name} onChange={e => setName(e.target.value)} placeholder="Full name" />
      </div>
      <div className="form-sep" />
      <div className="form-footer">
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button className="btn-primary" onClick={() => { if (name.trim()) onSave(user.email, name.trim()); }}>Save</button>
      </div>
    </div>
  );
}

function AddUserModal({ roles, onSave, onCancel }) {
  const [email, setEmail] = useState("");
  const [name,  setName]  = useState("");
  const [role,  setRole]  = useState("");
  return (
    <div className="modal">
      <div className="modal-header">
        <div className="modal-title">Add User</div>
        <button className="btn-small" onClick={onCancel}>✕</button>
      </div>
      <div className="form-row">
        <span className="form-label">Email</span>
        <input className="form-input" style={{ flex:1 }} value={email} onChange={e => setEmail(e.target.value)} placeholder="user@almehrajadhesives.com" />
      </div>
      <div className="form-row">
        <span className="form-label">Name</span>
        <input className="form-input" style={{ flex:1 }} value={name} onChange={e => setName(e.target.value)} placeholder="Full name" />
      </div>
      <div className="form-row">
        <span className="form-label">Role</span>
        <select className="form-select" style={{ flex:1 }} value={role} onChange={e => setRole(e.target.value)}>
          <option value="">Select role…</option>
          {roles.map(r => <option key={r.code} value={r.code}>{r.code} — {r.description||""}</option>)}
        </select>
      </div>
      <div className="form-sep" />
      <div className="form-footer">
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button className="btn-primary" onClick={() => { if (email) onSave({ email, name, roleCode:role||undefined }); }}>Add</button>
      </div>
    </div>
  );
}
